"use client";

import { useState, useEffect } from "react";

const steps = [
  {
    id: 1,
    title: "Profile Project",
    shortTitle: "Profile",
    subtitle: "Detect stack & frameworks",
    icon: "profile",
    cliOutput: [
      { text: "$ secureflow scan ./my-project", color: "command" },
      { text: "", color: "default" },
      { text: "✓ Profiling project structure", color: "success" },
      { text: "  Detected: Node.js + Express + React", color: "gray" },
      { text: "  Database: PostgreSQL", color: "gray" },
      { text: "  Auth: JWT tokens detected", color: "gray" },
      { text: "", color: "default" },
      { text: "Input :12.4k | Context Usage :8.2%", color: "stats" },
    ],
  },
  {
    id: 2,
    title: "Read Files",
    shortTitle: "Read",
    subtitle: "Smart file discovery",
    icon: "files",
    cliOutput: [
      { text: "✓ Reading source files", color: "success" },
      { text: "", color: "default" },
      { text: "Read(./src/api/users.ts)", color: "filepath" },
      { text: "  └ Analyze user API for auth vulnerabilities", color: "gray" },
      { text: "Read(./src/api/payments.ts)", color: "filepath" },
      { text: "  └ Check payment flow for injection risks", color: "gray" },
      { text: "Read(./src/db/queries.ts)", color: "filepath" },
      { text: "", color: "default" },
      { text: "Files: 52/52 analyzed", color: "info" },
    ],
  },
  {
    id: 3,
    title: "Build Code Graph",
    shortTitle: "Graph",
    subtitle: "AST + dataflow analysis",
    icon: "graph",
    cliOutput: [
      { text: "✓ Building code graph", color: "success" },
      { text: "", color: "default" },
      { text: "  Parsing AST nodes...", color: "gray" },
      { text: "  Resolving imports...", color: "gray" },
      { text: "  Mapping function calls...", color: "gray" },
      { text: "  Building dataflow edges...", color: "gray" },
      { text: "", color: "default" },
      { text: "Graph: 847 nodes, 2,341 edges", color: "info" },
      { text: "Entry points: 23 API routes", color: "info" },
    ],
  },
  {
    id: 4,
    title: "Source to Sink",
    shortTitle: "Trace",
    subtitle: "Trace data flow paths",
    icon: "flow",
    cliOutput: [
      { text: "✓ Tracing data flows", color: "success" },
      { text: "", color: "default" },
      { text: "  Source: req.body.userId", color: "cyan" },
      { text: "    → validateInput()", color: "cyan" },
      { text: "    → getUserById()", color: "cyan" },
      { text: "    → db.query() [SINK]", color: "warning" },
      { text: "", color: "default" },
      { text: "Found 3 potential paths", color: "info" },
    ],
  },
  {
    id: 5,
    title: "AI Model Validation",
    shortTitle: "Validate",
    subtitle: "Rules + context analysis",
    icon: "validate",
    cliOutput: [
      { text: "✓ Hunting bugs with the patience of a zen master", color: "success" },
      { text: "", color: "default" },
      { text: "Input :101.7k Output :6 | Context Usage :211.6%", color: "stats" },
      { text: "", color: "default" },
      { text: "claude-3.5-sonnet | 9 iterations | Total:270.8k", color: "model" },
      { text: "", color: "default" },
      { text: "Critical:1 High:0 Medium:2 Low:0", color: "severity" },
    ],
  },
  {
    id: 6,
    title: "Report & Export",
    shortTitle: "Export",
    subtitle: "SARIF, JSON, DefectDojo",
    icon: "report",
    cliOutput: [
      { text: "✓ Scan completed in 50.1s", color: "success" },
      { text: "  Files analyzed: 52/52", color: "gray" },
      { text: "  AI iterations: 9", color: "gray" },
      { text: "", color: "default" },
      { text: "Critical:1 High:0 Medium:2 Low:0 Info:0", color: "severity" },
      { text: "", color: "default" },
      { text: "Exported: results.sarif", color: "info" },
      { text: "✓ DefectDojo upload complete", color: "success" },
    ],
  },
];

function StepIcon({ icon, variant = "inactive", size = "normal" }: { icon: string; variant?: "active" | "past" | "inactive"; size?: "small" | "normal" }) {
  // active = white (on green bg), past = green (on light green bg), inactive = gray
  const color = variant === "active" ? "#ffffff" : variant === "past" ? "#10b981" : "#9ca3af";
  const className = size === "small" ? "w-4 h-4" : "w-6 h-6";

  switch (icon) {
    case "profile":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
          <rect x="4" y="4" width="16" height="16" rx="2" />
          <line x1="8" y1="9" x2="16" y2="9" />
          <line x1="8" y1="13" x2="14" y2="13" />
        </svg>
      );
    case "files":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
          <path d="M3 7V17C3 18.1 3.9 19 5 19H19C20.1 19 21 18.1 21 17V9C21 7.9 20.1 7 19 7H11L9 5H5C3.9 5 3 5.9 3 7Z" />
        </svg>
      );
    case "graph":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
          <circle cx="6" cy="6" r="3" />
          <circle cx="18" cy="6" r="3" />
          <circle cx="12" cy="18" r="3" />
          <line x1="8" y1="8" x2="10" y2="16" />
          <line x1="16" y1="8" x2="14" y2="16" />
        </svg>
      );
    case "flow":
      const isFlowActive = variant !== "inactive";
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
          <circle cx="5" cy="12" r="3" fill={isFlowActive ? "#ef4444" : "#d1d5db"} stroke={isFlowActive ? "#ef4444" : "#d1d5db"} />
          <circle cx="19" cy="12" r="3" fill={isFlowActive ? "#f59e0b" : "#d1d5db"} stroke={isFlowActive ? "#f59e0b" : "#d1d5db"} />
          <path d="M8 12H16" strokeDasharray="2,2" />
        </svg>
      );
    case "validate":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
          <path d="M12 2L3 7V12C3 17.5 6.8 22.7 12 24C17.2 22.7 21 17.5 21 12V7L12 2Z" />
          <path d="M9 12L11 14L15 10" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "report":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
          <rect x="5" y="3" width="14" height="18" rx="2" />
          <line x1="8" y1="8" x2="16" y2="8" />
          <line x1="8" y1="12" x2="14" y2="12" />
        </svg>
      );
    default:
      return null;
  }
}

export default function HowItWorksInteractive() {
  const [activeStep, setActiveStep] = useState(0);
  const [visibleLines, setVisibleLines] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  // Auto-advance steps
  useEffect(() => {
    if (isPaused) return;

    const stepDuration = 4000;
    const timer = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % steps.length);
      setVisibleLines(0);
    }, stepDuration);

    return () => clearInterval(timer);
  }, [isPaused]);

  // Animate CLI output lines
  useEffect(() => {
    const currentOutput = steps[activeStep].cliOutput;
    if (visibleLines >= currentOutput.length) return;

    const timer = setTimeout(() => {
      setVisibleLines((prev) => prev + 1);
    }, 100);

    return () => clearTimeout(timer);
  }, [activeStep, visibleLines]);

  // Reset visible lines when step changes
  useEffect(() => {
    setVisibleLines(0);
  }, [activeStep]);

  return (
    <div
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Visual Flow - Horizontal Steps */}
      <div className="mb-12 overflow-x-auto py-4">
        <div className="flex items-center justify-center min-w-[700px] gap-2">
          {steps.map((step, index) => {
            const isActive = index === activeStep;
            const isPast = index < activeStep;
            const isLast = index === steps.length - 1;

            return (
              <div key={step.id} className="flex items-center">
                {/* Step Node */}
                <button
                  onClick={() => {
                    setActiveStep(index);
                    setVisibleLines(0);
                  }}
                  className={`flex flex-col items-center transition-all duration-300 ${
                    isActive ? "scale-110" : "hover:scale-105"
                  }`}
                >
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 ${
                      isActive
                        ? "bg-white border-2 border-emerald-500 shadow-md"
                        : isPast
                        ? "bg-emerald-50 border-2 border-emerald-300"
                        : "bg-gray-50 border-2 border-gray-200"
                    }`}
                  >
                    <StepIcon icon={step.icon} variant={isActive ? "past" : isPast ? "past" : "inactive"} />
                  </div>
                  <span
                    className={`mt-2 text-xs font-medium transition-colors ${
                      isActive ? "text-emerald-600" : isPast ? "text-emerald-500" : "text-gray-400"
                    }`}
                  >
                    {step.shortTitle}
                  </span>
                </button>

                {/* Arrow */}
                {!isLast && (
                  <div className="flex items-center mx-2">
                    <div
                      className={`w-8 h-0.5 transition-colors ${
                        isPast ? "bg-emerald-400" : "bg-gray-200"
                      }`}
                    />
                    <div
                      className={`w-0 h-0 border-t-[5px] border-t-transparent border-b-[5px] border-b-transparent border-l-[6px] transition-colors ${
                        isPast ? "border-l-emerald-400" : "border-l-gray-200"
                      }`}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Detail View */}
      <div className="grid lg:grid-cols-2 gap-8 items-start">
        {/* Current Step Details */}
        <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-8 border border-emerald-200">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center border-2 border-emerald-500 shadow-md">
              <StepIcon icon={steps[activeStep].icon} variant="past" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium px-2.5 py-0.5 rounded-full bg-emerald-500 text-white">
                  Step {steps[activeStep].id}
                </span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mt-1">
                {steps[activeStep].title}
              </h3>
            </div>
          </div>
          <p className="text-gray-600 mb-6">{steps[activeStep].subtitle}</p>

          {/* Progress dots */}
          <div className="flex items-center gap-2">
            {steps.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  setActiveStep(index);
                  setVisibleLines(0);
                }}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === activeStep
                    ? "w-6 bg-emerald-500"
                    : index < activeStep
                    ? "bg-emerald-300"
                    : "bg-gray-300"
                }`}
              />
            ))}
          </div>
        </div>

        {/* CLI Terminal */}
        <div className="bg-gray-900 rounded-2xl overflow-hidden shadow-2xl">
          {/* Terminal header */}
          <div className="flex items-center gap-2 px-4 py-3 bg-gray-800 border-b border-gray-700">
            <div className="flex gap-1.5">
              <div className="w-3 h-3 rounded-full bg-red-500" />
              <div className="w-3 h-3 rounded-full bg-yellow-500" />
              <div className="w-3 h-3 rounded-full bg-green-500" />
            </div>
            <span className="text-gray-400 text-sm ml-2 font-mono">secureflow</span>
          </div>

          {/* Terminal content */}
          <div className="p-4 font-mono text-sm h-[280px] overflow-hidden">
            {steps[activeStep].cliOutput.slice(0, visibleLines).map((line, i) => {
              const colorClasses: Record<string, string> = {
                command: "text-emerald-400",
                success: "text-emerald-400",
                gray: "text-gray-400",
                default: "text-gray-300",
                filepath: "text-yellow-400",
                info: "text-blue-400",
                cyan: "text-cyan-400",
                warning: "text-amber-400",
                stats: "text-lime-400",
                model: "text-red-400",
                severity: "text-gray-300",
              };
              return (
                <div key={i} className={colorClasses[line.color] || "text-gray-300"}>
                  {line.text || "\u00A0"}
                </div>
              );
            })}
            {visibleLines < steps[activeStep].cliOutput.length && (
              <span className="inline-block w-2 h-4 bg-emerald-400 animate-pulse" />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
